import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { GoogleOAuthProvider, useGoogleLogin } from '@react-oauth/google';
import { Eye, EyeOff, ShieldCheck } from 'lucide-react';

import { loginUser, loginUserWithGoogle, verifyTwoFactor } from '../redux/authSlice';
import { ROUTE_REGISTER_USER, ROUTE_FORGOT_PASSWORD } from '../constants';
import { useToast } from '@/shared/components/toast-system';
import { getUserReadableError } from '../utils/utils';

import AuthLayout from '../components/AuthLayout';

const GOOGLE_CLIENT_ID = import.meta.env.REACT_APP_GOOGLE_CLIENT_ID;

const ArcSpinner = ({ className = 'auth-spinner' }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none">
    <circle className="track" cx="12" cy="12" r="9" strokeWidth="2.4" />
    <circle className="arc" cx="12" cy="12" r="9" strokeWidth="2.4" />
  </svg>
);

const CustomGoogleLogin = ({ status, disabled, onAuthResponse, onOpening, onExchanging, onReset }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const toast = useToast();

  const showError = (msg) => toast.show({ type: 'error-soft', title: msg });

  const handleGoogleSuccess = async (credentialResponse) => {
    onExchanging();
    try {
      const response = await dispatch(loginUserWithGoogle(credentialResponse, navigate, onAuthResponse));
      if (response?.action === 'verify_2fa') {
        onReset();
      }
    } catch (error) {
      console.error('Google login failed:', error);
      onReset();
      showError(getUserReadableError(error) ?? 'Google login failed');
    }
  };

  const handleGoogleError = () => {
    onReset();
    showError('Google login was unsuccessful. Please try again.');
  };

  const googleLogin = useGoogleLogin({
    onSuccess: handleGoogleSuccess,
    onError: handleGoogleError,
    onNonOAuthError: () => onReset(),
    flow: 'auth-code',
  });

  return (
    <button
      type="button"
      onClick={() => {
        onOpening();
        googleLogin();
      }}
      disabled={disabled}
      data-state={status}
      className="google-button"
    >
      <span className="google-btn-face google-btn-face--idle">
        <svg className="google-icon" version="1.1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48">
          <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"></path>
          <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.42-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"></path>
          <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"></path>
          <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"></path>
          <path fill="none" d="M0 0h48v48H0z"></path>
        </svg>
        <span>Continue with Google</span>
      </span>
      <span className="google-btn-face google-btn-face--load">
        <ArcSpinner />
        <span className="google-btn-load-text">Opening Google…</span>
      </span>
    </button>
  );
};

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const toast = useToast();

  const showError = (msg) => toast.show({ type: 'error-soft', title: msg });

  const [credentials, setCredentials] = useState({ email: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [emailStatus, setEmailStatus] = useState('idle');
  const [googleStatus, setGoogleStatus] = useState('idle');
  const [completing, setCompleting] = useState(false);

  const busy = emailStatus !== 'idle' || googleStatus !== 'idle' || completing;
  const busyKind = emailStatus !== 'idle' ? 'email' : googleStatus !== 'idle' || completing ? 'google' : '';

  const startGoogleOpening = () => setGoogleStatus('loading');
  const startGoogleExchange = () => setCompleting(true);
  const resetGoogle = () => {
    setGoogleStatus('idle');
    setCompleting(false);
  };

  const [is2FA, setIs2FA] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [tempUserId, setTempUserId] = useState(null);

  const handleAuthResponse = (response) => {
    if (response?.action === 'verify_2fa') {
      setVerificationCode('');
      setIs2FA(true);
      setTempUserId(response.userId);
      return true;
    }
    return false;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setEmailStatus('loading');
    const navigateWhenDone = (...args) => {
      setEmailStatus('done');
      setTimeout(() => navigate(...args), 900);
    };
    try {
      const response = await dispatch(loginUser(credentials, navigateWhenDone, handleAuthResponse));
      if (response?.action === 'verify_2fa') {
        setEmailStatus('idle');
      }
    } catch (error) {
      console.error('Login failed:', error);
      setEmailStatus('idle');
      showError(getUserReadableError(error) ?? 'Login failed');
    }
  };

  const handleVerify2FA = async (e) => {
    e.preventDefault();
    if (!verificationCode || !tempUserId) return;

    try {
      await dispatch(verifyTwoFactor({ userId: tempUserId, code: verificationCode }, navigate));
    } catch (error) {
      console.error('Verification failed:', error);
      showError(getUserReadableError(error) ?? 'Invalid code');
    }
  };

  if (is2FA) {
    return (
      <AuthLayout
        icon={<img src="/images/logo_trans.png" alt="TypeHero" className="auth-logo" />}
        title="Verification"
      >
        <div className="verification-icon">
          <ShieldCheck size={48} style={{ color: '#3b82f6' }} />
        </div>
        
        <div className="verification-description">
          We sent a 6-digit code to your email.<br/>Please enter it below to continue.
        </div>
        
        <form onSubmit={handleVerify2FA} className="auth-form">
          <div className="input-group">
            <label htmlFor="code" className="input-label">Verification Code</label>
            <input 
              type="text" 
              id="code" 
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').substring(0, 6))}
              placeholder="123456"
              autoComplete="one-time-code" 
              className="input-field verification-code-input"
              required
            />
          </div>
          
          <button type="submit" className="auth-button">Verify Device</button>
          
          <div className="auth-footer">
            <button 
              type="button" 
              className="back-button"
              onClick={() => setIs2FA(false)}
            >
              Back to Login
            </button>
          </div>
        </form>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      icon={<img src="/images/logo_trans.png" alt="TypeHero" className="auth-logo" />}
    >
      <div className="auth-stack" data-busy={busyKind} data-completing={completing ? '1' : '0'}>
        <div className="auth-google">
          <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
            <CustomGoogleLogin
              status={googleStatus}
              disabled={busy}
              onAuthResponse={handleAuthResponse}
              onOpening={startGoogleOpening}
              onExchanging={startGoogleExchange}
              onReset={resetGoogle}
            />
          </GoogleOAuthProvider>
        </div>

        <div className="auth-separator">
          <div className="auth-separator-line">
            <div className="auth-separator-border" />
          </div>
          <div className="auth-separator-text">
            <span className="auth-separator-span">or</span>
          </div>
        </div>

        <form onSubmit={(e) => handleSubmit(e)} className="auth-form">
          <div className="auth-fields">
            <div className="input-group">
              <label htmlFor="email" className="input-label">Email</label>
              <input
                type="email"
                id="email"
                value={credentials.email}
                onChange={(e) => setCredentials({ ...credentials, email: e.target.value })}
                autoComplete="username"
                className="input-field"
                disabled={busy}
                required
              />
            </div>

            <div className="input-group">
              <div className="password-label-row">
                <label htmlFor="password" className="input-label">Password</label>
                <a href={ROUTE_FORGOT_PASSWORD} className="forgot-password-link">
                  Forgot password?
                </a>
              </div>
              <div className="password-input-wrapper">
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  value={credentials.password}
                  onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                  autoComplete="current-password"
                  className="input-field"
                  disabled={busy}
                  required
                />
                <button
                  type="button"
                  className="password-toggle-btn"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={busy}
                  tabIndex="-1"
                  title={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff /> : <Eye />}
                </button>
              </div>
            </div>
          </div>

          <div className="pt-2">
            <button
              type="submit"
              className="auth-button auth-submit"
              data-state={emailStatus}
              disabled={busy}
            >
              <span className="auth-btn-face auth-btn-face--idle">Sign in</span>
              <span className="auth-btn-face auth-btn-face--load">
                <ArcSpinner />
                <span className="auth-btn-load-text">Signing you in…</span>
              </span>
              <span className="auth-btn-face auth-btn-face--done">
                <svg className="auth-check" viewBox="0 0 24 24">
                  <path d="M5 13 l4 4 L19 7" />
                </svg>
                <span className="auth-btn-load-text">Signed in</span>
              </span>
            </button>
          </div>
        </form>

        <div className="auth-footer pt-2">
          Don't have an account?{" "}
          <a href={ROUTE_REGISTER_USER} className="auth-footer-link">Sign up</a>
        </div>

        <div className="auth-completing" role="status" aria-live="polite" aria-busy="true">
          <ArcSpinner className="auth-spinner auth-spinner--lg" />
          <div className="auth-completing-msg">Completing sign-in</div>
          <div className="auth-completing-sub">Verifying your Google account…</div>
        </div>
      </div>
    </AuthLayout>
  );
};

export default Login;